import { Controller } from "@hotwired/stimulus"

/**
 * Controls the show/hide of API vs file upload fields in the broad source
 * discovery form based on the selected source type radio button.
 * Also handles auto-populating fields when a saved source connector is selected.
 */
export default class extends Controller {
  static targets = ["apiFields", "fileFields", "connectorSelect", "apiUrl"]

  declare readonly apiFieldsTarget: HTMLElement
  declare readonly fileFieldsTarget: HTMLElement
  declare readonly hasApiFieldsTarget: boolean
  declare readonly hasFileFieldsTarget: boolean
  declare readonly connectorSelectTarget: HTMLSelectElement
  declare readonly hasConnectorSelectTarget: boolean
  declare readonly apiUrlTarget: HTMLInputElement
  declare readonly hasApiUrlTarget: boolean

  connect(): void {
    // Ensure both sections are hidden on connect (no default selection)
    this.hideAll()
  }

  toggle(event: Event): void {
    const radio = event.currentTarget as HTMLInputElement
    const sourceType = radio.value

    if (sourceType === "api") {
      this.showApiFields()
    } else if (sourceType === "file") {
      this.showFileFields()
    }
  }

  fileSelected(event: Event): void {
    const input = event.currentTarget as HTMLInputElement
    const file = input.files?.[0]
    if (!file) return

    const validTypes = [
      "text/csv",
      "application/json",
      "application/vnd.ms-excel",
    ]
    const validExtensions = [".csv", ".json"]
    const hasValidExtension = validExtensions.some((ext) =>
      file.name.toLowerCase().endsWith(ext)
    )

    if (!validTypes.includes(file.type) && !hasValidExtension) {
      input.value = ""
      // Use alert as a simple approach; the server will also validate
      alert("Please select a CSV or JSON file.")
    }
  }

  connectorSelected(): void {
    if (!this.hasConnectorSelectTarget) return

    const select = this.connectorSelectTarget
    const selectedOption = select.options[select.selectedIndex]

    if (!selectedOption || !selectedOption.value) {
      // "Manual config" (blank) selected — reset to default state
      this.clearConnectorSelection()
      return
    }

    const url = selectedOption.dataset.url
    const connectorType = selectedOption.dataset.connectorType

    if (connectorType === "api") {
      this.selectRadio("api")
      this.showApiFields()
      if (this.hasApiUrlTarget && url) {
        this.apiUrlTarget.value = url
      }
    } else if (connectorType === "website") {
      // Website connectors don't use the API/file radio flow;
      // show API fields section with the URL pre-filled for context
      this.selectRadio("api")
      this.showApiFields()
      if (this.hasApiUrlTarget && url) {
        this.apiUrlTarget.value = url
      }
    }
  }

  private clearConnectorSelection(): void {
    this.hideAll()
    this.deselectRadios()
    if (this.hasApiUrlTarget) {
      this.apiUrlTarget.value = ""
    }
  }

  private selectRadio(value: string): void {
    const radio = this.element.querySelector(
      `input[type="radio"][name="source_type"][value="${value}"]`
    ) as HTMLInputElement | null
    if (radio) {
      radio.checked = true
    }
  }

  private deselectRadios(): void {
    const radios = this.element.querySelectorAll(
      'input[type="radio"][name="source_type"]'
    ) as NodeListOf<HTMLInputElement>
    radios.forEach((radio) => {
      radio.checked = false
    })
  }

  private hideAll(): void {
    if (this.hasApiFieldsTarget) {
      this.apiFieldsTarget.classList.add("hidden")
    }
    if (this.hasFileFieldsTarget) {
      this.fileFieldsTarget.classList.add("hidden")
    }
  }

  private showApiFields(): void {
    if (this.hasApiFieldsTarget) {
      this.apiFieldsTarget.classList.remove("hidden")
    }
    if (this.hasFileFieldsTarget) {
      this.fileFieldsTarget.classList.add("hidden")
    }
  }

  private showFileFields(): void {
    if (this.hasApiFieldsTarget) {
      this.apiFieldsTarget.classList.add("hidden")
    }
    if (this.hasFileFieldsTarget) {
      this.fileFieldsTarget.classList.remove("hidden")
    }
  }
}
